// `shortlistEventPublisherService` — the four flag events (`OnCandidateFavor`, `OnCandidateHidden`
// and their client-contact twins, with the inverses) that a PROFILE emits and the REVIEW list
// behind it consumes.
//
// ⚠️ WITHOUT THIS THE TWO DISAGREE. The profile renders as the review's child route, so the review
// stays mounted underneath it with its own copy of the row; a star or an archive from the profile
// changed the profile's icon and left the row it came from showing the old state until a reload.
// Upstream's review applies the same client-side flag change on each event; this is that wire, as
// small as it can be, in the shape of `tabNames.ts`.
import { useEffect, useRef } from 'react'

export type ShortlistEntity = 'candidates' | 'clientContacts'

export type ShortlistFlagChange =
  | { kind: 'favor' }
  | { kind: 'unfavor' }
  | { kind: 'unhide' }
  | { kind: 'hide'; hiddenDate: number }

export type ShortlistFlagEvent = ShortlistFlagChange & {
  entity: ShortlistEntity
  alertId: number
  ids: readonly number[]
}

const listeners = new Set<(event: ShortlistFlagEvent) => void>()

export function emitShortlistFlagEvent(event: ShortlistFlagEvent): void {
  for (const listener of listeners) listener(event)
}

/** Subscribe for the component's lifetime; the latest `onEvent` is always the one called. */
export function useShortlistFlagEvents(onEvent: (event: ShortlistFlagEvent) => void): void {
  const latest = useRef(onEvent)
  latest.current = onEvent
  useEffect(() => {
    const listener = (event: ShortlistFlagEvent): void => {
      latest.current(event)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])
}

/**
 * `OnSingleCandidateMessageClick` / `OnSingleClientContactMessageClick` — a profile INSIDE the
 * review asking the review to message its entity. Upstream's profile never navigates for this: the
 * list handles it (`messageCandidate`, `messageClientContact:787`), ADDING the entity to the
 * selection and opening `…-messages?alertId=…`, whose recipients ARE the selection. The port's
 * profile navigated by itself, kept its own `?candidateId`/`?clientContactId`, and left the
 * selection empty — so the composer opened with nobody to send to (measured 29 Oct).
 *
 * `row` is the profile's own copy, for an entity that is not in the list's loaded rows (a profile
 * opened by URL); upstream crashes there (`clientContactMap[id]` is undefined), the port does not.
 */
export interface ShortlistMessageRequest {
  entity: ShortlistEntity
  alertId: number
  row: { id: number } & object
}

const messageListeners = new Set<(request: ShortlistMessageRequest) => void>()

/** True when a review handled it; the caller falls back to its own composer otherwise. */
export function requestShortlistMessage(request: ShortlistMessageRequest): boolean {
  for (const listener of messageListeners) listener(request)
  return messageListeners.size > 0
}

export function useShortlistMessageRequests(onRequest: (request: ShortlistMessageRequest) => void): void {
  const latest = useRef(onRequest)
  latest.current = onRequest
  useEffect(() => {
    const listener = (request: ShortlistMessageRequest): void => {
      latest.current(request)
    }
    messageListeners.add(listener)
    return () => {
      messageListeners.delete(listener)
    }
  }, [])
}

/**
 * `OnSingleCandidateExportClick` / `OnSingleClientContactExportClick` (cee70a51e8) — a profile
 * INSIDE the review asking the review to export its entity. The snippet's Export is "handled by
 * whichever parent owns this snippet": the hunter list (`onExportCandidate`, which ticks the entity
 * and exports the selection exactly as a row's Export does) or, on the standalone profile, the
 * profile state itself. This is the first of those two; the second never needs an event here.
 */
export type ShortlistExportRequest = ShortlistMessageRequest

const exportListeners = new Set<(request: ShortlistExportRequest) => void>()

/** True when a review handled it. */
export function requestShortlistExport(request: ShortlistExportRequest): boolean {
  for (const listener of exportListeners) listener(request)
  return exportListeners.size > 0
}

export function useShortlistExportRequests(onRequest: (request: ShortlistExportRequest) => void): void {
  const latest = useRef(onRequest)
  latest.current = onRequest
  useEffect(() => {
    const listener = (request: ShortlistExportRequest): void => {
      latest.current(request)
    }
    exportListeners.add(listener)
    return () => {
      exportListeners.delete(listener)
    }
  }, [])
}
